
import type { ReactNode } from "react";
import type { ColumnDescription, ServerSetting } from "../../types/session.ts";
import { ModuleId } from "../../types/session.ts";
import styles from "./cellRenderer.module.scss";
import { StatusIcon, type StatusIconKind } from "../ui/statusIcon.tsx";
import { getClientLocale, getClientTimeZone, getTimeZoneAbbreviation } from "../../util/clientEnv.ts";
import { getServerOffsetMinutesAt } from "../../util/serverTime.ts";
import { resolveKioskZone } from "../../util/kioskZone.ts";
import { colFromLinkView } from "./hyperlinkDispatcher.ts";


export interface ButtonClickInfo {
  rowData: string[];
  pageName: string;
  type: number;
  label: string;
}

interface CommonCacheItem {
  sName?: string;
  sFilterValue?: string;
  sDisplayText?: string;
  TypeName?: string;
  Value?: string;
  Title?: string;
}

function ccName(c: CommonCacheItem): string { return c.TypeName ?? c.sName ?? ""; }
function ccValue(c: CommonCacheItem): string { return c.Value ?? c.sFilterValue ?? ""; }
function ccTitle(c: CommonCacheItem): string { return c.Title ?? c.sDisplayText ?? ""; }

export interface CellRenderContext {
  rowData?: unknown[];
  colIndex?: Record<string, number>;
  commonData?: unknown[];
  onHyperlinkClick?: (linkId: number, linkView: string, displayType: string, value: unknown) => void;
  ticketStatuses?: unknown[];
  priceTypes?: unknown[];
  csIssues?: unknown[];
  csIssueTypes?: unknown[];
  serverSettings?: ServerSetting[];
  /** Name of the grid that owns this cell — surfaced in dev warnings so a
   *  missing onButtonClick handler can be traced back to its call site. */
  gridName?: string;
  /** Active endpoint's module id (see `ModuleId` enum). Drives module-specific
   *  hardcoded fallbacks for cells like IPRICETYPEID where WebAdmin2 had
   *  per-module enums; the cache-driven lookup is still tried first. */
  moduleId?: number;
  /** Full column-description map keyed by lower-cased column name. Required by
   *  TEXTCOLLECTION cells, which compose their display from sibling columns and
   *  render each sub-value using that column's own DisplayType converter. */
  colInfoMap?: Record<string, ColumnDescription>;
  /** Sort the grid by a sibling column. Wired to the grid's header-click sort
   *  so TEXTCOLLECTION internal headers can re-order the grid. The grid guards
   *  un-sortable columns internally, so calling this is always safe. */
  onSortColumn?: (columnName: string) => void;
  /** Current sort direction marker (▲/▼) for a column, mirroring the real
   *  column header indicator. Used by TEXTCOLLECTION internal headers. */
  sortIndicator?: (columnName: string) => ReactNode;
}

/**
 * Frames the rendered cell when its raw value is the empty string — an
 * *intentional override* of an inherited default, distinct from null/undefined
 * (which simply renders blank). Used by the TEXTBOXEMPTY DisplayType and
 * exposed for reuse by gridOverride decorators in pages that can't change the
 * column's server-side DisplayType.
 */
export function decorateEmptyOverride(node: ReactNode, rawValue: unknown): ReactNode {
  return rawValue === "" ? <span className={styles.emptyOverride}>{node}</span> : node;
}

/**
 * Display types where the auto-tooltip would be more confusing than helpful.
 * Split into two cases (collapsed into one set):
 *
 *   1. The on-screen affordance already IS the value (checkbox is the bool,
 *      button label IS the label) so a duplicate tooltip is noise.
 *   2. The cell renderer transforms the raw value into a human-readable
 *      display (status code → "Critical Error", lookup id → title). Showing
 *      the raw value (e.g. "0") as a tooltip below "Critical Error" is
 *      actively misleading. Matches WebAdmin2 columnvalue.ts — its
 *      StatusTextColor / ValuedComboBox / IssueType / IssueText / KioskStatusText
 *      cells never emit a title attribute.
 *
 * Renderers that legitimately need their own tooltip (BoolColor, StatusIcon,
 * GDPR mask, Activity) attach a <c>title</c> on their innermost span; the
 * browser picks the innermost title on hover, so omitting an outer title
 * here doesn't lose anything.
 *
 * A non-empty <c>LinkView</c> overrides the skip — see resolveCellTooltip.
 * That is the "specialized tooltip, using LinkView if tooltip should be
 * other data field" path: a column with a custom DisplayType can still opt
 * a sibling field in as the tooltip source.
 */
const TOOLTIP_SKIP_DISPLAY_TYPES = new Set<string>([
  // Interactive / pure-visual:
  "CHECKBOX",
  "CHECKBOXDISABLED",
  "CHECKBOXCONFIRM",
  "UNCHECKBOX",
  "DATELOCK",
  "DATELOCKUNLOCK",
  "BUTTON",
  "STATUSIMAGE",   // inner StatusIcon already has title={label}
  "BOOLCOLOR",     // inner span already has title="Yes"/"No"
  "ACTIVITY",      // inner span already has title=absolute-timestamp
  "LASTSTATUS",    // (same)
  "GDPR", "GDPRE", "GDPR@", "GDPRE@",   // inner span has title=unmasked
  "HYPERLINK", "HYPERLINKPARAMS", "HYPERLINKPAGEPARAMS", "HYPERLINKNOTE",
  "STRICTHYPERLINK",
  // Renderer transforms code/id → display text; raw tooltip is misleading:
  "STATUSTEXTCOLOR",
  "VALUEDCOMBOBOX",
  "ISSUETYPE",
  "ISSUETEXT",
  "KIOSKSTATUSTEXT",
]);

/**
 * Date/time display types. Their raw cell value is a JS Date, so the default
 * String(raw) tooltip would render in the BROWSER's local zone — diverging from
 * the cell, which formats through getClientTimeZone() and therefore honours an
 * active SA Client-timezone override. The tooltip is formatted with the same
 * timezone (see formatDateTooltip) so hover matches the cell.
 */
const DATE_DISPLAY_TYPES = new Set<string>([
  "ADMINTIME", "KIOSKTIME", "KIOSKDATE", "INSTANTDATE",
  "UNTYPEDDATETIME", "UNTYPEDDATE", "UNTYPEDDATEMONTH",
  "DATETIMEOFFSET", "UTCTOADMINTIME",
]);

/**
 * Default cell tooltip resolver — surfaces the raw value (or a sibling field
 * pointed at by <c>LinkView</c>) so the user can still read the underlying
 * data when the visible cell shows a formatted/truncated version.
 *
 * <ul>
 *   <li><c>LinkView</c> always wins. If the column metadata names a sibling
 *       column (same parser as <c>hyperlinkDispatcher.colFromLinkView</c> —
 *       first plain, non-<c>@</c>, non-<c>#</c> reference), that column's
 *       value becomes the tooltip even on transformed-display cells.
 *       Matches WebAdmin2 Text(): <c>extra.info.LinkView</c> overrides the
 *       cell's own value when set.</li>
 *   <li>Otherwise, transformed-display cell types in
 *       <c>TOOLTIP_SKIP_DISPLAY_TYPES</c> get no outer tooltip (the
 *       renderer either supplies its own inner title or has nothing
 *       useful to add — same as WebAdmin2's behaviour).</li>
 *   <li>Otherwise falls back to the raw cell value. Useful for truncated
 *       text columns and date cells (where the raw ISO string is more
 *       precise than the locale-formatted display).</li>
 *   <li>Returns <c>undefined</c> when the resolved value is empty/null so
 *       React omits the <c>title</c> attribute entirely (no empty hover
 *       tooltips on blank cells).</li>
 * </ul>
 */
export function resolveCellTooltip(
  rawValue: unknown,
  colInfo: ColumnDescription | undefined,
  rowData: unknown[] | undefined,
  colIndex: Record<string, number> | undefined,
): string | undefined {
  // LinkView always wins — explicit "tooltip should be other data field" path.
  if (colInfo?.LinkView && rowData && colIndex) {
    const v = colFromLinkView(colInfo.LinkView, rowData, colIndex);
    if (v != null && v !== "") return String(v);
  }
  const displayType = colInfo?.DisplayType?.toUpperCase();
  if (displayType && TOOLTIP_SKIP_DISPLAY_TYPES.has(displayType)) return undefined;
  if (rawValue == null || rawValue === "") return undefined;
  // Date cells: format the tooltip in the SAME timezone the cell uses (honouring
  // an SA Client-timezone override) instead of String(Date), which would render
  // in the browser's local zone and show a different time than the cell.
  if (displayType && DATE_DISPLAY_TYPES.has(displayType)) {
    return formatDateTooltip(rawValue, displayType, { rowData, colIndex });
  }
  return String(rawValue);
}

/** Renders a cell value based on the column's DisplayType. */
export function renderCellValue(
  value: unknown,
  colInfo: ColumnDescription | undefined,
  onButtonClick?: (info: ButtonClickInfo) => void,
  context?: CellRenderContext,
): ReactNode {
  const raw = value ?? "";
  const displayType = colInfo?.DisplayType?.toUpperCase();

  if (!displayType) {
    return String(raw);
  }

  switch (displayType) {
    case "CHECKBOX":
    case "CHECKBOXDISABLED":
    case "CHECKBOXCONFIRM":
      return renderCheckBox(raw, displayType === "CHECKBOXDISABLED");
    case "UNCHECKBOX":
      return renderCheckBox(raw, false, true);
    case "DATELOCK":
      return renderDateLock(raw);
    case "BOOLCOLOR":
      return renderBoolColor(raw);
    case "ADMINTIME":
    case "KIOSKTIME":
    case "KIOSKDATE":
    case "INSTANTDATE":
    case "UNTYPEDDATETIME":
    case "UNTYPEDDATE":
    case "UNTYPEDDATEMONTH":
    case "DATETIMEOFFSET":
    case "UTCTOADMINTIME":
      return renderDate(raw, displayType, context);
    case "STATUSIMAGE":
      return renderStatusImage(raw, colInfo);
    case "HYPERLINK":
    case "HYPERLINKPARAMS":
    case "HYPERLINKPAGEPARAMS":
    case "HYPERLINKNOTE":
      return renderHyperlink(raw, colInfo, displayType, context);
    case "STRICTHYPERLINK":
      return renderStrictHyperlink(raw);
    case "KIOSKSTATUSTEXT":
      return raw ? "Live" : "Test";
    case "STATUSTEXTCOLOR":
      return renderStatusTextColor(raw, colInfo, context);
    case "ACTIVITY":
      return renderActivity(raw, "No activity", "Online");
    case "LASTSTATUS":
      return renderActivity(raw, "No recent data", "<1 Hour");
    case "DATELOCKUNLOCK":
      return renderDateLockUnlock(raw, colInfo);
    case "ISSUETYPE":
      return renderIssueType(raw, context);
    case "ISSUETEXT":
      return renderIssueText(raw, context);
    case "VALUEDCOMBOBOX":
      return renderValuedComboBox(raw, colInfo, context);
    case "TEXTBLOCKPRICE":
    case "TEXTBOX":
    case "TEXTBOXPRICE":
    case "TEXTAREA":
    case "MAILADDRESS":
      return String(raw);
    case "TEXTCOLLECTION":
      return renderTextCollection(colInfo, context) ?? String(raw);
    case "TEXTBOXVERIFIED":
    case "TEXTBOXEMPTY":
      return decorateEmptyOverride(String(raw), value);
    case "BUTTON":
      return renderButton(raw, onButtonClick, context?.gridName);
    case "GDPR":
    case "GDPRE":
      return renderGdpr(raw);
    case "GDPRE@":
    case "GDPR@":
      return renderGdprAt(raw);
    default:
      return String(raw);
  }
}

function toBool(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  switch (String(value).toLowerCase()) {
    case "0":
    case "false":
    case "no":
      return false;
    default:
      return true;
  }
}

function renderCheckBox(value: unknown, disabled: boolean, inverted?: boolean): ReactNode {
  const checked = inverted ? !toBool(value) : toBool(value);
  return <input type="checkbox" checked={checked} disabled={disabled} readOnly />;
}

function renderDateLock(value: unknown): ReactNode {
  const hasDate = value != null && String(value) !== "" && String(value) !== "false";
  return <input type="checkbox" checked={hasDate} disabled readOnly />;
}

function renderBoolColor(value: unknown): ReactNode {
  const isTrue = toBool(value);
  return (
    <span
      className={`${styles.boolDot} ${isTrue ? styles.boolTrue : styles.boolFalse}`}
      title={isTrue ? "Yes" : "No"}
    />
  );
}

/** A zoneless SQL/ISO timestamp — "2026-08-13T07:10:32.51" or with a space. */
const ZONELESS_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * DisplayTypes whose stored value is *already* UTC rather than SQL-server wall
 * clock. Everything downstream assumes the latter, so these need the assumption
 * undone — see parseCellDate.
 */
const UTC_SOURCE_DISPLAY_TYPES = new Set<string>(["UTCTOADMINTIME"]);

/**
 * Turns a raw cell value into the instant it actually represents.
 *
 * Grid cells arrive as real Date objects: DataGrid.vb `QuickWrite` writes
 * `time.SqlServerTimeToUtc.Ticks` for every datetime, i.e. it treats the stored
 * wall clock as the server's own zone and converts to UTC, and gridDataParser
 * turns those ticks back into a Date. For columns that really do hold
 * SQL-server wall clock that is correct.
 *
 * For UTC_SOURCE_DISPLAY_TYPES it is not: the value was already UTC, so the
 * serializer subtracted an offset that was never there. Adding the server's
 * offset back recovers the stored wall clock as the UTC instant it was meant to
 * be, and the toLocaleString(timeZone) below then shifts it into the viewer's
 * zone. On a UTC server nothing was subtracted and this adds nothing.
 *
 * JSON-fed cells arrive as zoneless strings instead; those parse as
 * browser-local, so the same columns need an explicit Z. Legacy WebAdmin2 did
 * the string form of this in ConvertDateFromUtcToUserTime (`value + " UTC"`),
 * but off browser-local rather than the server zone — wrong outside Sweden.
 */
function parseCellDate(raw: unknown, displayType?: string): Date {
  const isUtcSource = !!displayType && UTC_SOURCE_DISPLAY_TYPES.has(displayType);
  if (raw instanceof Date) {
    if (!isUtcSource || isNaN(raw.getTime())) return raw;
    return new Date(raw.getTime() + getServerOffsetMinutesAt(raw) * 60000);
  }
  const s = String(raw);
  if (isUtcSource && ZONELESS_DATETIME_RE.test(s)) {
    return new Date(s.replace(" ", "T") + "Z");
  }
  return new Date(s);
}

/**
 * DisplayTypes with no intrinsic instant: the stored value is zoneless and the cell
 * must show those digits back, whoever is looking.
 *
 * Only the UNTYPED* family qualifies. Everything else — including KIOSKTIME,
 * KIOSKDATE and INSTANTDATE — holds the SQL server's clock, which *is* an instant
 * once the server's zone is known, so those render in the viewer's zone.
 *
 * Kiosk* is easy to misread: a kiosk in the database's own zone produces rows whose
 * digits match the timestamp inside the InstantID, which looks like proof the value
 * is kiosk-local. It isn't — the two clocks just agree.
 */
const WALL_CLOCK_DISPLAY_TYPES = new Set<string>([
  "UNTYPEDDATETIME", "UNTYPEDDATE", "UNTYPEDDATEMONTH",
]);

/**
 * Recovers the stored wall clock as a Date whose *UTC* fields hold those digits,
 * so formatting with `timeZone: "UTC"` reproduces them exactly.
 *
 * The binary grid transport has already turned the wall clock into an instant by
 * assuming the server's zone (`SqlServerTimeToUtc`), so undoing that assumption —
 * not the browser's — is what gets the digits back. Reading them off the browser's
 * clock instead only worked because dev boxes share a zone with their database;
 * against a UTC server a Stockholm viewer saw every untyped value two hours late.
 *
 * JSON-fed cells arrive as zoneless strings, which are the wall clock already.
 */
function wallClockCellDate(raw: unknown): Date {
  if (raw instanceof Date) {
    return new Date(raw.getTime() + getServerOffsetMinutesAt(raw) * 60000);
  }
  const s = String(raw).replace(" ", "T");
  if (ZONELESS_DATETIME_RE.test(s) || /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(s + (s.length === 10 ? "T00:00:00Z" : "Z"));
  }
  // Carries a zone, so it is an instant; express it in the server's zone, which is
  // the frame the stored value was written in.
  const d = new Date(s);
  return isNaN(d.getTime()) ? d : new Date(d.getTime() + getServerOffsetMinutesAt(d) * 60000);
}

/** "YYYY-MM" off a wall-clock Date's UTC fields. */
function utcMonth(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** KIOSKTIME / KIOSKDATE answer "what time was it at the kiosk", so they convert
 *  into the kiosk's zone rather than the viewer's — see util/kioskZone.ts. */
const KIOSK_ZONE_DISPLAY_TYPES = new Set<string>(["KIOSKTIME", "KIOSKDATE"]);

/**
 * The row's kiosk id, from whichever column carries it. Same lookup WebAdmin2's
 * KioskTime converter did (`ikioskid` / `kioskid`); without row context — a nested
 * TEXTCOLLECTION sub-cell, or the Date Gallery — there is nothing to resolve.
 */
function kioskIdFromContext(context?: CellRenderContext): number | null {
  const { rowData, colIndex } = context ?? {};
  if (!rowData || !colIndex) return null;
  for (const key of ["ikioskid", "kioskid"]) {
    const ix = colIndex[key];
    if (ix === undefined) continue;
    const n = Number(rowData[ix]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function formatDateString(
  raw: unknown,
  includeTime: boolean,
  monthOnly?: boolean,
  untyped?: boolean,
  displayType?: string,
  context?: CellRenderContext,
): string {
  if (raw === null || raw === undefined || raw === "") return "";
  const loc = getClientLocale();

  if (untyped || monthOnly || (displayType && WALL_CLOCK_DISPLAY_TYPES.has(displayType))) {
    const w = wallClockCellDate(raw);
    if (isNaN(w.getTime())) return String(raw);
    if (monthOnly) return utcMonth(w);
    // Locale still drives ordering and separators; UTC only pins the digits.
    return includeTime
      ? w.toLocaleString(loc, { timeZone: "UTC" })
      : w.toLocaleDateString(loc, { timeZone: "UTC" });
  }

  const d = parseCellDate(raw, displayType);
  if (isNaN(d.getTime())) return String(raw);

  // Kiosk zone when the row lets us find it, with the abbreviation appended so a
  // kiosk-time cell is distinguishable from the viewer-zone cells beside it. No
  // kiosk, no location, or no zone on the location falls back to the viewer's zone —
  // same as every other column, rather than silently showing a third frame unlabelled.
  if (displayType && KIOSK_ZONE_DISPLAY_TYPES.has(displayType)) {
    const kioskId = kioskIdFromContext(context);
    const zone = kioskId === null ? null : resolveKioskZone(kioskId, d);
    if (zone) {
      const text = includeTime
        ? d.toLocaleString(loc, { timeZone: zone.iana })
        : d.toLocaleDateString(loc, { timeZone: zone.iana });
      return zone.abbreviation ? `${text} ${zone.abbreviation}` : text;
    }
  }

  // Viewer's zone. Timestamps carry its abbreviation so a reader can tell which
  // clock they are looking at; date-only cells stay bare.
  const tz = getClientTimeZone();
  if (includeTime) {
    const abbr = getTimeZoneAbbreviation(tz, d, loc);
    const text = d.toLocaleString(loc, { timeZone: tz });
    return abbr ? `${text} ${abbr}` : text;
  }
  return d.toLocaleDateString(loc, { timeZone: tz });
}

/**
 * Tooltip text for a date cell: the full timestamp in the SAME timezone the
 * cell renders in (impersonation-aware via getClientTimeZone), with the zone
 * name appended so the hover is unambiguous. Mirrors renderDate's per-type
 * rules — untyped/wall-clock and date-only types stay zoneless.
 */
function formatDateTooltip(raw: unknown, displayType: string, context?: CellRenderContext): string | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  const loc = getClientLocale();
  const includeTime = displayType !== "UNTYPEDDATE" &&
    displayType !== "KIOSKDATE" &&
    displayType !== "INSTANTDATE";

  // Wall-clock types: same digit-preserving treatment as the cell, so hover agrees.
  if (WALL_CLOCK_DISPLAY_TYPES.has(displayType)) {
    const w = wallClockCellDate(raw);
    if (isNaN(w.getTime())) return String(raw);
    if (displayType === "UNTYPEDDATEMONTH") return utcMonth(w);
    return includeTime
      ? w.toLocaleString(loc, { timeZone: "UTC" })
      : w.toLocaleDateString(loc, { timeZone: "UTC" });
  }

  const d = parseCellDate(raw, displayType);
  if (isNaN(d.getTime())) return String(raw);

  // Kiosk zone, so hover agrees with the cell instead of quietly showing the
  // viewer's zone for a cell rendered in the kiosk's.
  if (KIOSK_ZONE_DISPLAY_TYPES.has(displayType)) {
    const kioskId = kioskIdFromContext(context);
    const zone = kioskId === null ? null : resolveKioskZone(kioskId, d);
    if (zone) {
      const text = includeTime
        ? d.toLocaleString(loc, { timeZone: zone.iana })
        : d.toLocaleDateString(loc, { timeZone: zone.iana });
      return zone.abbreviation ? `${text} ${zone.abbreviation}` : text;
    }
  }

  const tz = getClientTimeZone();
  if (!includeTime) return d.toLocaleDateString(loc, { timeZone: tz });
  const abbr = getTimeZoneAbbreviation(tz, d, loc);
  const text = d.toLocaleString(loc, { timeZone: tz });
  return abbr ? `${text} ${abbr}` : text;
}

function renderDate(raw: unknown, displayType: string, context?: CellRenderContext): ReactNode {
  const monthOnly = displayType === "UNTYPEDDATEMONTH";
  const includeTime = displayType !== "UNTYPEDDATE" &&
    displayType !== "KIOSKDATE" &&
    displayType !== "INSTANTDATE" &&
    !monthOnly;
  const untyped = displayType.startsWith("UNTYPED");
  const formatted = formatDateString(raw, includeTime, monthOnly, untyped, displayType, context);
  // Return plain text (no wrapping span) so the cellInner's
  // `:not(:has(> *))` rule treats the date as a pure-text cell and the
  // sub-pixel cap-alignment nudge applies.
  return formatted;
}

type StatusEntry = { kind: StatusIconKind; label: string };

const statusImageLookup: Record<string, StatusEntry> = {
  // Kiosk status
  OK:        { kind: "ok",      label: "Kiosk should be fine" },
  Green:     { kind: "ok",      label: "Kiosk should be fine" },
  MAINT:     { kind: "error",   label: "Kiosk is experiencing an issue" },
  MANAGER:   { kind: "paused",  label: "Kiosk is in KioskManager" },
  Yellow:    { kind: "warning", label: "Warning" },
  "Not Delivered": { kind: "warning", label: "Not Delivered" },
  // Payment
  Bad:       { kind: "error",   label: "Payment Issues" },
  Good:      { kind: "ok",      label: "Fully Paid" },
  Lost:      { kind: "error",   label: "Lost" },
  Open:      { kind: "okLight", label: "Open" },
  "Not Payed":   { kind: "warning", label: "Not Paid" },
  "Payment Late":{ kind: "warning", label: "Payment Late" },
  // Tickets
  TicketNew:        { kind: "info",     label: "New" },
  TicketOpen:       { kind: "progress", label: "Open" },
  TicketMonitoring: { kind: "paused",   label: "Monitoring" },
  TicketAuto:       { kind: "pending",  label: "Auto Generated" },
  TicketClosed:     { kind: "ok",       label: "Closed" },
  TicketFault:      { kind: "error",    label: "Has Tickets" },
  TicketOk:         { kind: "ok",       label: "No Ticket" },
  // Inventory
  InvOrdered:   { kind: "info",     label: "Ordered" },
  InvInstalled: { kind: "progress", label: "Installed" },
  InvTesting:   { kind: "paused",   label: "Testing" },
  InvReady:     { kind: "progress", label: "Ready" },
  InvDelivered: { kind: "ok",       label: "Delivered" },
  // InvApp
  InvAppDelivered:  { kind: "ok",       label: "Delivered" },
  DELIVERED:        { kind: "ok",       label: "Delivered" },
  InvAppDelivering: { kind: "progress", label: "Delivering" },
  DELIVERING:       { kind: "progress", label: "Delivering" },
  InvAppPickedUp:   { kind: "progress", label: "Picked Up" },
  PICKEDUP:         { kind: "progress", label: "Picked Up" },
  InvAppPacked:     { kind: "info",     label: "Packed" },
  PACKED:           { kind: "info",     label: "Packed" },
  InvAppPacking:    { kind: "progress", label: "Packing" },
  PACKING:          { kind: "progress", label: "Packing" },
  InvAppWaiting:    { kind: "pending",  label: "Waiting" },
  WAITING:          { kind: "pending",  label: "Waiting" },
  // Replenish
  ReplenishOrdered:          { kind: "info",     label: "Ordered" },
  ReplenishPartiallyStocked: { kind: "progress", label: "Partially Stocked" },
  ReplenishPartiallyFinished:{ kind: "progress", label: "Partially Finished" },
  ReplenishFinished:         { kind: "ok",       label: "Finished" },
  // Warehouse / pickup
  Reserved:     { kind: "pending",  label: "Reserved" },
  Canceled:     { kind: "blocked",  label: "Canceled" },
  DropOff:      { kind: "info",     label: "DropOff" },
  StaffPickUp:  { kind: "progress", label: "StaffPickUp" },
  StaffDropOff: { kind: "info",     label: "StaffDropOff" },
  PickUp:       { kind: "ok",       label: "PickUp" },
  DropOffRetrieve: { kind: "progress", label: "DropOffRetrieve" },
};

const projectStatusLookup: Record<number, StatusEntry> = {
  0: { kind: "warning", label: "Active - Opportunity" },
  1: { kind: "progress", label: "Active - Project Ongoing" },
  2: { kind: "blocked", label: "Passive - Opportunity" },
  3: { kind: "ok",      label: "Completed" },
};

function renderStatusImage(raw: unknown, colInfo?: ColumnDescription): ReactNode {
  const val = String(raw ?? "").trim();

  // Special handling for IPROJECTSTATUS column
  if (colInfo?.ColumnName?.toUpperCase() === "IPROJECTSTATUS") {
    const info = projectStatusLookup[Number(val)];
    if (info) return <StatusIcon kind={info.kind} label={info.label} />;
    return val;
  }

  // MUD:N pattern — "Unit(s) down: N"
  if (val.startsWith("MUD:")) {
    const count = val.split(":")[1] ?? "";
    return <StatusIcon kind="warning" label={`Unit(s) down: ${count}`} />;
  }

  const info = statusImageLookup[val];
  if (info) return <StatusIcon kind={info.kind} label={info.label} />;

  // Unknown status
  if (val) return <StatusIcon kind="unknown" label={val} />;
  return "";
}

function renderHyperlink(
  raw: unknown,
  colInfo: ColumnDescription | undefined,
  displayType: string,
  context?: CellRenderContext,
): ReactNode {
  const hasHandler = context?.onHyperlinkClick && colInfo?.LinkID;
  const handleClick = hasHandler
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        context!.onHyperlinkClick!(colInfo!.LinkID, colInfo!.LinkView ?? "", displayType, raw);
      }
    : undefined;

  return (
    <span
      className={styles.hyperlink}
      onClick={handleClick}
      style={hasHandler ? { cursor: "pointer" } : undefined}
    >
      {String(raw)}
    </span>
  );
}

function renderStrictHyperlink(raw: unknown): ReactNode {
  const url = String(raw);
  return (
    <a href={url} target="_blank" rel="noreferrer">
      {url}
    </a>
  );
}

function censorGdpr(value: string): string {
  if (value.length > 15) {
    return value.substring(0, 3) + "****" + value.substring(value.length - 3);
  }
  if (value.length > 9) {
    return value.substring(0, 2) + "****" + value.substring(value.length - 2);
  }
  if (value.length > 4) {
    return value.substring(0, 1) + "****" + value.substring(value.length - 1);
  }
  return "****";
}

function renderGdpr(raw: unknown): ReactNode {
  const val = String(raw);
  if (!val) return "";
  return <span title={val}>{censorGdpr(val)}</span>;
}

function renderGdprAt(raw: unknown): ReactNode {
  const val = String(raw);
  if (val.includes("@")) {
    return renderGdpr(raw);
  }
  return val;
}

function renderValuedComboBox(
  raw: unknown,
  colInfo: ColumnDescription | undefined,
  context?: CellRenderContext,
): ReactNode {
  if (!colInfo || !context?.commonData) return String(raw);
  const lookupKey = colInfo.ColumnFormat
    ? (colInfo.ColumnFormat.split("|")[0] ?? colInfo.ColumnName)
    : colInfo.ColumnName;
  const keyLower = lookupKey.toLowerCase();
  const rawStr = String(raw);
  const common = context.commonData as CommonCacheItem[];
  const match = common.find(
    (item) => ccName(item).toLowerCase() === keyLower && ccValue(item) === rawStr,
  );
  return match ? (ccTitle(match) || ccValue(match) || rawStr) : rawStr;
}

interface TextCollectionCell {
  column: number;
  row: number;
  columnName: string;
  header: string;
  bold: boolean;
}

/**
 * Renders a TEXTCOLLECTION cell — a mini layout that pulls values from sibling
 * columns of the same row and arranges them in a grid of label/value pairs.
 * Mirrors WebAdmin2 columnvalue.ts `TextCollection`.
 *
 * The layout is encoded in the column's <c>LinkView</c>, fields separated by
 * <c>¤</c> (legacy) or <c>§</c>:
 *   <c>1-180|3§col|row|columnName|Header:§…</c>
 *   • field[0] = <c>minWidth-maxWidth|cols</c> header config; the max-width
 *     number is used as the per-column min-width (px).
 *   • each following field = <c>column|row|siblingColumnName|Header[|bold]</c>.
 *
 * Cells are grouped by <c>column</c> (rendered as side-by-side stacks) and
 * ordered by <c>row</c> within each stack. Each sibling value is rendered with
 * its own DisplayType converter (so dates format, etc.); a nested TEXTCOLLECTION
 * sibling is rendered as plain text to avoid infinite recursion.
 *
 * Returns <c>null</c> when it can't render (no LinkView, no row context), so the
 * caller can fall back to the raw value.
 */
function renderTextCollection(
  colInfo: ColumnDescription | undefined,
  context?: CellRenderContext,
): ReactNode {
  const linkView = colInfo?.LinkView;
  const rowData = context?.rowData;
  const colIndex = context?.colIndex;
  if (!linkView || !rowData || !colIndex) return null;

  const fields = linkView.split(/[¤§]/);
  if (fields.length < 2) return null;

  // Header config "1-180|3" → per-column min-width = 180px.
  let minWidth: number | undefined;
  const headerMatch = /^(\d+)-(\d+)\|(\d+)$/.exec((fields[0] ?? "").trim());
  if (headerMatch) {
    const w = parseInt(headerMatch[2]!, 10);
    if (!isNaN(w) && w > 0) minWidth = w;
  }

  const cells: TextCollectionCell[] = [];
  for (let i = 1; i < fields.length; i++) {
    const parts = (fields[i] ?? "").split("|");
    if (parts.length < 3) continue;
    const columnName = (parts[2] ?? "").trim();
    if (!columnName) continue;
    cells.push({
      column: parseInt((parts[0] ?? "0").split("-")[0] ?? "0", 10) || 0,
      row: parseInt((parts[1] ?? "0").split("-")[0] ?? "0", 10) || 0,
      columnName,
      header: parts[3] ?? "",
      bold: !!(parts[4] && parts[4] !== "0"),
    });
  }
  if (cells.length === 0) return null;

  const findIdx = (cn: string): number => {
    if (cn in colIndex) return colIndex[cn]!;
    const lc = cn.toLowerCase();
    for (const k in colIndex) if (k.toLowerCase() === lc) return colIndex[k]!;
    return -1;
  };

  // Group by column, ordered ascending; rows ordered ascending within a column.
  const groups = new Map<number, TextCollectionCell[]>();
  for (const c of cells) {
    const g = groups.get(c.column);
    if (g) g.push(c);
    else groups.set(c.column, [c]);
  }
  const sortedColumns = [...groups.keys()].sort((a, b) => a - b);

  return (
    <div className={styles.textCollection}>
      {sortedColumns.map((colNo) => {
        const group = groups.get(colNo)!.slice().sort((a, b) => a.row - b.row);
        return (
          <div
            key={colNo}
            className={styles.textCollectionCol}
            style={minWidth ? { minWidth } : undefined}
          >
            {group.map((c, ix) => {
              const idx = findIdx(c.columnName);
              const subVal = idx >= 0 ? rowData[idx] : undefined;
              const subInfo = context?.colInfoMap?.[c.columnName.toLowerCase()];
              const subDt = subInfo?.DisplayType?.toUpperCase();
              const rendered = subInfo && subDt && subDt !== "TEXTCOLLECTION"
                ? renderCellValue(subVal, subInfo, undefined, context)
                : String(subVal ?? "");
              const onSortColumn = context?.onSortColumn;
              const headerNode = c.header
                ? onSortColumn
                  ? (
                    <button
                      type="button"
                      className={styles.textCollectionHeaderBtn}
                      title={`Sort by ${c.header}`}
                      onClick={(e) => { e.stopPropagation(); onSortColumn(c.columnName); }}
                    >
                      {c.header}
                      {context?.sortIndicator?.(c.columnName)}
                    </button>
                  )
                  : <span className={styles.textCollectionLabel}>{c.header} </span>
                : null;
              const content = (
                <>
                  {headerNode}
                  {rendered}
                </>
              );
              return (
                <div key={ix} className={styles.textCollectionItem}>
                  {c.bold ? <b>{content}</b> : content}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Grid-row button icons — single source of truth in `statusIcon.tsx`.
// `buttonIconInline` keeps the 16×16 sizing + display:block from the
// previous inline-SVG rendering.
const mailIcon = <StatusIcon kind="mail" size={16} className={styles.buttonIconInline} />;
const viewIcon = <StatusIcon kind="view" size={16} className={styles.buttonIconInline} />;
const pushIcon = <StatusIcon kind="push" size={16} className={styles.buttonIconInline} />;
const editIcon = <StatusIcon kind="edit" size={16} className={styles.buttonIconInline} />;
const deleteIcon = <StatusIcon kind="trash" size={16} className={styles.buttonIconInline} />;

const inlineButtonIcons: Record<string, ReactNode> = {
  view: viewIcon,
  push: pushIcon,
  edit: editIcon,
  delete: deleteIcon,
  "delete group": deleteIcon,
  "delete item": deleteIcon,
  remove: deleteIcon,
  "resend email": mailIcon,
};

/**
 * Inline-SVG replacements for server-supplied image filenames — theme-aware.
 * Kind assignments mirror statusImageLookup so the same filename keeps its visual semantic
 * whether the server sends it via a BUTTON column (buttonInfo[5]) or a STATUSIMAGE column.
 */
export const customInlineMap: Record<string, StatusIconKind> = {
  // Grid status — kiosk silhouette + corner badge keeps each state uniquely identifiable.
  "gridstatusok.png":          "kioskOk",
  "gridstatuserror.png":       "kioskError",
  "gridstatuskm.png":          "kioskManager",
  "gridstatuswarning.png":     "kioskWarning",
  "gridstatusunknown.png":     "kioskUnknown",
  "gridstatussomeerror.png":   "kioskSomeError",
  // Payment — $ disk + corner badge.
  "approved.png":              "paidOk",
  "denied.png":                "paidDenied",
  "open.png":                  "paidOpen",
  "paymentissue.png":          "paidIssue",
  "notpaid.png":               "paidNot",
  "paymentlate.png":           "paidLate",
  // Tickets — tag silhouette + corner badge.
  "ticketnew.png":             "ticketNew",
  "ticketopen.png":            "ticketOpen",
  "ticketmonitoring.png":      "ticketMonitor",
  "ticketauto.png":            "ticketAuto",
  "ticketclosed.png":          "ticketDone",
  "ticketfault.png":           "ticketFault",
  // InvApp — box cube + corner badge.
  "invappdelivered.png":       "boxDone",
  "invappdelivering.png":      "boxMoving",
  "invapppickedup.png":        "boxPickedUp",
  "invapppacked.png":          "boxPacked",
  "invapppacking.png":         "boxPacking",
  "invappwaiting.png":         "boxWaiting",
  // Replenish — progress ring filled in 0 / 33 / 66 / 100%.
  "replenishordered.png":          "replenishOrdered",
  "replenishpartiallystocked.png": "replenishPartial1",
  "replenishpartiallyfinished.png":"replenishPartial2",
  "replenishfinished.png":         "replenishDone",
  // Domain-specific
  "Faulty_Transaction.png": "faultyTransaction",
  "event.png":              "event",
  // Button / action
  "cog-edit.png":           "cogEdit",
  "cog-delete.png":         "cogDelete",
  "unread.png":             "mailUnread",
  "read.png":               "mailRead",
  "solved.png":             "solved",
  "unsolved.png":           "unsolved",
};


function renderButton(
  raw: unknown,
  onButtonClick?: (info: ButtonClickInfo) => void,
  gridName?: string,
): ReactNode {
  const val = String(raw);
  const parts = val.split("@ip=");
  if (parts.length < 2) return "";

  const rowData = parts[0]!.split("¤");
  const buttonInfo = parts[1]!.split("¤");
  if (buttonInfo.length < 4) return "";

  const pageName = buttonInfo[0]!;
  const type = parseInt(buttonInfo[1]!) || 0;
  const label = buttonInfo[2] ?? "";
  const show = buttonInfo[3] ? parseInt(buttonInfo[3]) : 1;

  if (!show) return "";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onButtonClick) {
      console.warn(
        `[cellRenderer] BUTTON cell clicked on grid '${gridName ?? "<unknown>"}' but the parent DataGrid did not provide an onButtonClick handler — the click will do nothing. Wire onButtonClick on the <DataGrid name="${gridName ?? ""}"/> render site.`,
        { gridName, pageName, type, label, rowData },
      );
      return;
    }
    onButtonClick({ rowData, pageName, type, label });
  };

  // Resolution order (all inline SVG — no PNG imports):
  // 1. Server-supplied filename → customInlineMap → StatusIcon kind
  // 2. Label → inlineButtonIcons → inline SVG JSX
  // 3. Default → edit pencil icon
  const customInlineKind = buttonInfo[5] ? customInlineMap[buttonInfo[5]] : undefined;
  const labelKey = label.toLowerCase();
  const inlineIcon = !customInlineKind ? (inlineButtonIcons[labelKey] ?? editIcon) : undefined;

  return (
    <button
      type="button"
      onClick={handleClick}
      title={label}
      aria-label={label}
      className={styles.gridButton}
    >
      {customInlineKind
        ? <StatusIcon kind={customInlineKind} size={20} />
        : inlineIcon}
    </button>
  );
}

// --- Constants for StatusTextColor lookups ---

const ReplenishStatuses: { ID: number; Title: string }[] = [
  { ID: 1, Title: "Ordered" },
  { ID: 2, Title: "PartiallyStocked" },
  { ID: 3, Title: "PartiallyFinished" },
  { ID: 4, Title: "Finished" },
];

const ReservationStatuses: { ID: number; Title: string }[] = [
  { ID: 1, Title: "Reserved" },
  { ID: 2, Title: "Canceled" },
  { ID: 3, Title: "DropOff" },
  { ID: 4, Title: "StaffPickUp" },
  { ID: 5, Title: "StaffDropOff" },
  { ID: 6, Title: "PickUp" },
  { ID: 7, Title: "DropOffRetrieve" },
];

/**
 * Module-specific hardcoded fallback labels for the IPRICETYPEID column.
 * Mirrors WebAdmin2 columnvalue.ts switch ("IPRICETYPEID"). The cache lookup
 * in renderStatusTextColor wins when it produces a TypeName; this kicks in
 * only when the cache has no entry for the given id (e.g. legacy ids that
 * never made it into S_Generic_Price_Type, or a fresh endpoint without the
 * common-data row yet).
 */
function pricetypeHardcodedFallback(id: number, moduleId: number | undefined): string | undefined {
  if (moduleId === ModuleId.CompuShop) {
    switch (id) {
      case 3: return "Purchase";
      case 6: return "Promotion";
      default: return undefined;
    }
  }
  // Access (12) and AIM (13) share the Access product schema (see
  // WebCache.Product.vb CommandsAccess) so they share the price-type enum.
  if (moduleId === ModuleId.Access || moduleId === ModuleId.AIM) {
    switch (id) {
      case -1: return "Correction Rental";
      case -3: return "Correction Purchase";
      case 1:  return "Rental";
      case 3:  return "Purchase";
      case 6:  return "Promotion";
      case 16: return "Refund";
      case 23: return "Package Deal";
      case 24: return "Package Deal Reporting";
      case 35: return "Earned Points";
      case 36: return "Subsidy";
      case 37: return "Subsidy Reporting";
      case 40: return "External Reduction";
      case 41: return "External Reduction Reporting";
      case 42: return "External Tender";
      case 43: return "External Tender Reporting";
      case 44: return "Promotion Reporting";
      case 45: return "Reserve";
      default: return undefined;
    }
  }
  return undefined;
}

function getServerSettingInt(
  settings: ServerSetting[] | undefined,
  key: string,
  fallback: number,
): number {
  if (!settings) return fallback;
  for (const s of settings) {
    if (s.Key === key) {
      const v = Number(s.Value);
      return isNaN(v) ? fallback : v;
    }
  }
  return fallback;
}

function renderStatusTextColor(
  raw: unknown,
  colInfo: ColumnDescription | undefined,
  context?: CellRenderContext,
): ReactNode {
  const val = raw ?? "";
  const colName = (colInfo?.ColumnName ?? "").toUpperCase();
  let text: string = String(val);
  // "" = inherit from cell (respects theme). Only set when the status has a real semantic color.
  let color = "";
  let bold = false;

  switch (colName) {
    case "IBARCODESTATUS":
    case "IPLACEMENTSTATUS":
    case "IPRODUCTSTATUS":
      switch (parseInt(String(val))) {
        case 0: text = "Critical Error"; color = "red"; break;
        case 1: text = "Ok"; color = "green"; break;
        case 2: text = "Error on arrival"; color = "#ff6400"; break;
        default: text = String(val); break;
      }
      break;

    case "ITRANSACTIONTYPE":
      switch (parseInt(String(val))) {
        case 1: text = "Direct Sale"; break;
        case 2: text = "Order"; break;
        case 3: text = "Prescription"; break;
        default: text = ""; break;
      }
      break;

    case "TICKETSTATUS": {
      const id = parseInt(String(val));
      const statuses = context?.ticketStatuses as { ID: number; Title: string }[] | undefined;
      const match = statuses?.find(x => x.ID === id);
      text = match ? match.Title : String(val);
      break;
    }

    case "REPLENISHSTATUS": {
      const id = parseInt(String(val));
      const match = ReplenishStatuses.find(x => x.ID === id);
      text = match ? match.Title : String(val);
      break;
    }

    case "RESERVATIONSTATUS": {
      const id = parseInt(String(val));
      const match = ReservationStatuses.find(x => x.ID === id);
      text = match ? match.Title : String(val);
      break;
    }

    case "IPRICETYPEID": {
      const id = parseInt(String(val));
      const types = context?.priceTypes as { ID?: number; TypeName?: string; PriceTypeID?: number }[] | undefined;
      const match = types?.find(x => (x.ID ?? x.PriceTypeID) === id);
      // Prefer cache value when present — operators can rename/extend
      // PriceTypes per module without rebuilding the client. Module-specific
      // hardcoded fallback below only kicks in when the cache has no entry
      // for this id, mirroring the per-module enums WebAdmin2 hardcoded for
      // CompuShop and Access.
      if (match?.TypeName) {
        text = match.TypeName;
        break;
      }
      const fallback = pricetypeHardcodedFallback(id, context?.moduleId);
      text = fallback ?? String(val);
      break;
    }

    case "ICOMPANYGROUP":
    case "ICONTACTPROJECT":
    case "IACTIVITYTYPE":
      text = String(val);
      break;

    case "IINSTANTRESULTCODE":
      switch (parseInt(String(val))) {
        case 0: text = "Approved"; break;
        case 1: text = "Declined"; break;
        case 2: text = "Failed"; break;
        case 3: text = "Fraud"; break;
        case 4: text = "ReturnOnly"; break;
        case 5: text = "ServerError"; break;
        case 6: text = "ValidationError"; break;
        case 7: text = "NetworkError"; break;
        case 8: text = "DuplicateDetected"; break;
        default: text = ""; break;
      }
      break;

    case "IREFILDELTA":
    case "ISTARTDELTA":
      text = String(val);
      color = parseInt(String(val)) > 0 ? "green" : "red";
      break;

    case "INITIALCHARGE":
    case "LATECHARGE":
      text = String(val);
      switch (String(val).toUpperCase()) {
        case "LOST": color = "red"; break;
        case "DISC OUT": color = "#ff6400"; break;
        case "PAID": color = "green"; break;
        default: text = ""; break;
      }
      break;

    case "SBLOCKREASON":
      if (val) {
        const overrideIdx = context?.colIndex?.["bBlockOverridden"] ?? context?.colIndex?.["bblockoverridden"];
        if (overrideIdx !== undefined && context?.rowData) {
          const overridden = context.rowData[overrideIdx];
          color = toBool(overridden) ? "#28b428" : "";
        }
        text = String(val);
      }
      break;

    case "IUNREADABLEDISCS": {
      const setting = getServerSettingInt(context?.serverSettings, "UnreadableDiscs", 0);
      color = Number(val) > setting ? "red" : "lightgreen";
      text = String(val);
      bold = true;
      break;
    }

    case "IBADDISCCOUNT": {
      const setting = getServerSettingInt(context?.serverSettings, "BadDiscsCount", 0);
      color = Number(val) > setting ? "red" : "lightgreen";
      text = String(val);
      bold = true;
      break;
    }

    case "IMISSINGPRODUCTIDCOUNT": {
      const setting = getServerSettingInt(context?.serverSettings, "MissingTitleDiscs", 0);
      color = Number(val) > setting ? "red" : "lightgreen";
      text = String(val);
      bold = true;
      break;
    }

    case "PRODUCTSTATUS":
      switch (String(val)) {
        case "-1": text = "CRITICAL"; color = "red"; bold = true; break;
        case "0": text = "WARNING"; color = "gold"; bold = true; break;
        case "1": text = "OK"; color = "green"; bold = true; break;
        default: text = ""; break;
      }
      break;

    case "ISINTRUDER":
    case "EDITEDCARTMANUALLY":
    case "MARKEDINSPECTION":
      switch (String(val).trim()) {
        case "1": text = "YES"; color = "red"; bold = true; break;
        case "0": case "-1": text = "NO"; color = "green"; bold = true; break;
        default: text = ""; break;
      }
      break;

    case "REPLENISHFINISHED":
      switch (String(val)) {
        case "-1": text = "NO"; color = "green"; bold = true; break;
        case "1": text = "YES"; color = "green"; bold = true; break;
        case "0": text = "NO"; color = "red"; bold = true; break;
        default: text = ""; break;
      }
      break;

    case "OPERATORISTHIRDPARTY":
      switch (String(val)) {
        case "true": text = "YES"; color = "green"; bold = true; break;
        case "false": text = "NO"; color = ""; bold = true; break;
        default: text = ""; break;
      }
      break;

    case "ISCHECKEDIN":
      switch (String(val)) {
        case "Checked in":
          return <span style={{ color: "mediumseagreen", fontWeight: "bold" }}>Checked In</span>;
        case "Checked out":
          return <span style={{ color: "cornflowerblue", fontWeight: "bold" }}>Checked Out</span>;
        default:
          return <span style={{ color: "dimgray", fontWeight: "bold" }}>Unknown Status</span>;
      }

    case "COLOREDPAYMENTDATE":
      return <span style={{ color: "cornflowerblue" }}>{String(val)}</span>;

    case "COLOREDROWLATEPRICE":
    case "COLOREDROWLATETAX":
      return <span style={{ color: "cornflowerblue", fontWeight: "bold" }}>{String(val)}</span>;

    case "BSOLD":
      switch (String(val).toLowerCase()) {
        case "true": case "1":
          return <span style={{ color: "cornflowerblue", fontWeight: "bold" }}>Sold</span>;
        case "false": case "0":
          return <span style={{ color: "mediumseagreen", fontWeight: "bold" }}>No</span>;
        default:
          return <span style={{ color: "dimgray", fontWeight: "bold" }}>Unknown Status</span>;
      }

    case "ISADVERTISEMENTACTIVE":
      switch (String(val)) {
        case "0": text = "Not active"; color = "red"; bold = true; break;
        case "1": text = "Active"; color = "green"; bold = true; break;
        default:
          return <span style={{ color: "dimgray", fontWeight: "bold" }}>Unknown Status</span>;
      }
      break;

    case "EVENTRESOLVED":
    case "ISVIDEODOWNLOADED":
      switch (String(val)) {
        case "0": text = "NO"; color = ""; break;
        case "1": text = "YES"; color = "green"; bold = true; break;
        default: text = ""; break;
      }
      break;

    case "FRENTALS30PERCENTAGE":
    case "FRENTALS7PERCENTAGE":
    case "FRENTALS2PERCENTAGE":
    case "FRENTALS1PERCENTAGE": {
      const f = parseFloat(String(val));
      if (f < -40) color = "#C80000";
      else if (f > 40) color = "#60A800";
      else color = "";
      text = isNaN(f) ? String(val) : f.toFixed(2) + "%";
      break;
    }

    case "WARNING":
      color = "red";
      text = String(val);
      break;

    case "REPLENISHDELETED":
      if (val === undefined || val === null || String(val) === "0") {
        return <span style={{ color: "green" }}>NO</span>;
      } else if (String(val) === "1") {
        return <span style={{ color: "red" }}>YES</span>;
      }
      break;

    default:
      text = String(val);
      break;
  }

  const el = bold ? <b>{text}</b> : <>{text}</>;
  // No color override → render plain (inherits cell/theme color). Avoids forcing black in dark mode.
  if (!color) return el;
  return <span style={{ color }}>{el}</span>;
}

function renderActivity(
  raw: unknown,
  nullLabel: string,
  recentLabel: string,
): ReactNode {
  if (!raw) {
    return <span className={styles.activity} title={nullLabel}>{nullLabel}</span>;
  }
  const d = new Date(String(raw));
  if (isNaN(d.getTime())) {
    return <span className={styles.activity}>{String(raw)}</span>;
  }
  const timeString = d.toLocaleString(getClientLocale(), { timeZone: getClientTimeZone() });
  const diff = Date.now() - d.getTime();
  let label: string;
  if (diff < 3600000) {
    label = recentLabel;
  } else if (diff < 86400000) {
    label = Math.floor(diff / 3600000) + " Hours";
  } else {
    label = Math.floor(diff / 86400000) + " Days";
  }
  return <span className={styles.activity} title={timeString}>{label}</span>;
}

function renderDateLockUnlock(
  raw: unknown,
  colInfo?: ColumnDescription,
): ReactNode {
  const val = String(raw ?? "");
  const isThinned = colInfo?.ColumnName?.toLowerCase() === "dthindateedit";
  const hasValue = val !== "" && val !== "false" && val !== "Active";

  if (isThinned) {
    if (hasValue) {
      const formatted = formatDateString(raw, true);
      return (
        <span>
          <input type="checkbox" checked readOnly /> {formatted}
        </span>
      );
    }
    return (
      <span>
        <input type="checkbox" checked={false} readOnly /> <i>not thinned</i>
      </span>
    );
  }

  if (hasValue) {
    return (
      <span>
        <input type="checkbox" checked readOnly /> {val}
      </span>
    );
  }
  return (
    <span>
      <input type="checkbox" checked={false} readOnly /> Active
    </span>
  );
}

function renderIssueType(raw: unknown, context?: CellRenderContext): ReactNode {
  const id = parseInt(String(raw));
  if (isNaN(id)) return String(raw ?? "");
  const types = context?.csIssueTypes as { ID?: number; CustomerIssueType?: string }[] | undefined;
  const match = types?.find(x => x.ID === id);
  return match?.CustomerIssueType ?? String(raw);
}

function renderIssueText(raw: unknown, context?: CellRenderContext): ReactNode {
  const id = parseInt(String(raw));
  if (isNaN(id)) return String(raw ?? "");
  const issues = context?.csIssues as { ID?: number; IssueTitle?: string }[] | undefined;
  const match = issues?.find(x => x.ID === id);
  return match?.IssueTitle ?? String(raw);
}
